import React, { useState } from 'react';
import './CorporateSolutionPage.css';

const fallbackImage = '/dts-assets/ai/corporate-hero.png';

const handleImageError = (fallback) => (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallback;
};

const HeroBlock = ({ label, title, text, image, chipOne = 'Fleet Dashboard', chipTwo = 'Smart Reports' }) => (
  <section className="enhanced-hero" style={{ backgroundImage: `url(${image})` }}>
    <div className="container enhanced-hero-grid">
      <div className="enhanced-copy reveal-left">
        <span className="section-label eyebrow-dark">{label}</span>
        <h1 dangerouslySetInnerHTML={{ __html: title }} />
        <p>{text}</p>

        <div className="hero-actions">
          <a href="/contact-us" className="btn btn-primary">Get Free Consultation</a>
          <a href="/packages" className="btn btn-outline">View Packages</a>
        </div>

        <div className="hero-metrics">
          <span><strong>24/7</strong>Monitoring</span>
          <span><strong>Live</strong>Dashboard</span>
          <span><strong>Smart</strong>Reports</span>
        </div>
      </div>

      <div className="hero-visual-wrap reveal">
        <div className="floating-orbit" />
        <div className="float-chip one"><b />{chipOne}</div>
        <div className="float-chip two"><b />{chipTwo}</div>

        <div className="hero-visual">
          <img src={image} alt={label} onError={handleImageError(fallbackImage)} />
        </div>
      </div>
    </div>
  </section>
);

const corporateSolutions = [
  {
    title: 'Fleet Tracking',
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80',
    text: 'Track every vehicle in real time with live location, route history, movement status, and alerts.',
    details: ['Real-time GPS vehicle location', 'Route history and playback', 'Geofence and movement alerts', 'Fleet dashboard visibility']
  },
  {
    title: 'Supply Chain Management',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=900&q=80',
    text: 'Improve delivery movement, route control, dispatch coordination, and shipment visibility.',
    details: ['Delivery movement visibility', 'Route and dispatch control', 'Shipment status monitoring', 'Better logistics coordination']
  },
  {
    title: 'Vehicle Video Monitoring',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
    text: 'Monitor vehicles with video telematics, dashcam support, incident visibility, and safety review.',
    details: ['4G dashcam monitoring', 'Live video support', 'Incident recording', 'Driver safety review']
  },
  {
    title: 'Fuel Monitoring',
    image: 'https://images.unsplash.com/photo-1542367597-8849eb950fd8?auto=format&fit=crop&w=900&q=80',
    text: 'Monitor fuel level, consumption, refueling, fuel drops, leakage, and possible fuel theft.',
    details: ['Fuel level monitoring', 'Fuel theft and leakage alerts', 'Refill and drop reports', 'Fuel usage analytics']
  },
  {
    title: 'Temperature Monitoring',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=900&q=80',
    text: 'Ideal for cold chain and sensitive cargo with real-time temperature alerts and reports.',
    details: ['Live temperature tracking', 'Cold chain monitoring', 'Threshold-based alerts', 'Temperature history reports']
  },
  {
    title: 'Load Monitoring',
    image: 'https://images.unsplash.com/photo-1586528116493-a029325540fa?auto=format&fit=crop&w=900&q=80',
    text: 'Track load status and improve control over cargo handling, delivery safety, and fleet usage.',
    details: ['Load status visibility', 'Cargo movement control', 'Delivery safety improvement', 'Operational usage reports']
  },
  {
    title: 'Driver Profiling & Safety Violations',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=900&q=80',
    text: 'Monitor overspeeding, harsh driving, fatigue, rest-time, seatbelt violations, and driver behavior.',
    details: ['Overspeed alerts', 'Harsh driving behavior', 'Fatigue and rest-time monitoring', 'Seatbelt violation reporting']
  },
  {
    title: 'Vehicle Maintenance Module',
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=900&q=80',
    text: 'Manage service reminders, maintenance records, vehicle health, and reduce unexpected downtime.',
    details: ['Service reminders', 'Vehicle maintenance records', 'Downtime control', 'Fleet health tracking']
  },
  {
    title: 'Lease Management Module',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80',
    text: 'Useful for leased fleets, rental operations, vehicle assignment, tracking, and usage control.',
    details: ['Lease vehicle tracking', 'Vehicle assignment records', 'Rental fleet control', 'Usage monitoring']
  },
  {
    title: 'Smart Mobile App Tracking',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=900&q=80',
    text: 'Access tracking, reports, alerts, vehicle status, and controls directly from the mobile app.',
    details: ['Mobile live tracking', 'Vehicle status updates', 'Alerts and notifications', 'Reports on mobile']
  },
  {
    title: 'Asset Tracker',
    image: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=900&q=80',
    text: 'Track valuable assets, equipment, generators, containers, machinery, and movable business resources.',
    details: ['Asset location monitoring', 'Equipment tracking', 'Generator and container tracking', 'Movement alerts']
  }
];

const DetailModal = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <div className="solution-modal-backdrop" onClick={onClose}>
      <div className="solution-modal" onClick={(e) => e.stopPropagation()}>
        <button className="solution-modal-close" onClick={onClose} type="button">×</button>

        <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />

        <div className="solution-modal-content">
          <span className="section-label">Corporate Solution</span>
          <h2>{item.title}</h2>
          <p>{item.text}</p>

          <ul>
            {item.details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>

          <a href="/contact-us" className="btn btn-primary">Get This Solution</a>
        </div>
      </div>
    </div>
  );
};

const CorporateSolutionPage = () => {
  const [selectedSolution, setSelectedSolution] = useState(null);

  return (
    <main className="enhanced-page corporate-enhanced">
      <HeroBlock
        label="Corporate Solutions"
        title="It's All About <em>Customers</em>"
        text="DTS corporate solutions help businesses monitor fleets, drivers, fuel, assets, supply chain, temperature, vehicle video, and maintenance from one reliable platform."
        image="/dts-assets/ai/corporate-hero.png"
      />

      <section className="enhanced-section alt">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">Corporate Solutions</span>
            <h2>Smart Business Tracking <em>Modules</em></h2>
            <div className="divider" />
            <p>
              Complete GPS tracking and fleet management modules designed for corporate fleets,
              logistics companies, rentals, leasing operations, and commercial vehicle owners.
            </p>
          </div>

          <div className="solution-media-grid">
            {corporateSolutions.map((item) => (
              <button
                className="solution-media-card reveal"
                key={item.title}
                type="button"
                onClick={() => setSelectedSolution(item)}
              >
                <div className="solution-media-img">
                  <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />
                </div>

                <div className="solution-media-body">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <span>View Details</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="enhanced-section dark-band">
        <div className="container split-grid">
          <div className="feature-panel feature-panel-readable reveal-left">
            <span className="section-label">Business Impact</span>
            <h2>Reduce Leakage. Improve Delivery. Increase Visibility.</h2>
            <p>
              Commercial fleets face fuel theft, driver misconduct, route delays, downtime,
              and compliance pressure. DTS turns fleet data into practical decisions with
              live alerts, reports, and support.
            </p>
          </div>

          <div className="media-card reveal">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
              alt="Corporate tracking dashboard"
              onError={handleImageError(fallbackImage)}
            />
          </div>
        </div>
      </section>

      <DetailModal item={selectedSolution} onClose={() => setSelectedSolution(null)} />
    </main>
  );
};

export default CorporateSolutionPage;